import React, { useState, useEffect, useCallback } from 'react';
import { planningOrauxAPI } from '../services/api';
import { AccordeonMail } from './StageParametrageContacts';
// les styles de l'éditeur vivent sous « .stg-root » (Suivi de stage) : l'éditeur est posé dans un hôte qui les porte
import './SuiviStage.css';

/**
 * PLANNING ORAUX — les sous-onglets « MODÈLES DE COURRIEL » et « JOURNAL DES ENVOIS » (lot PO.5a, D15, v79)
 * =========================================================================================================
 * Les trois modèles (candidats, maîtres selon l'étudiant, jurys) vivent dans la table COMMUNE du Stage (même éditeur,
 * mêmes versions, même « texte d'origine ») et sont COMMUNS AU DÉPARTEMENT ; ils s'écrivent par les routes du Planning
 * oraux (droits de CE module). Ils préremplissent la fenêtre d'envoi (PO.5b) : ce qu'on y change vaut pour un envoi.
 * Le journal est celui du Stage (`stages_envois`), rattaché au planning.
 */
const VARS_COMMUNES = ['{salutation}', '{e}', '{prenom_nom_etudiant}', '{intitule_oral}', '{date_oral}', '{heure_convocation}', '{heure_debut}', '{heure_fin}', '{salle}', '{contact}', '{nom_utilisateur}'];
export const MODELES = {
    po_candidat: {
        icone: 'fa-user-graduate', destinataire: 'Chaque étudiant (un message personnel)',
        sousTitre: 'Son jour, son horaire, sa salle, son jury — sa convocation et le rendez-vous joints',
        variables: [...VARS_COMMUNES, '{jury}'],
        ccSupplementaires: [{ cle: 'cc_maitre', libelle: 'Cc : son maître (copie de son message)' }],
        options: [{ cle: 'joindre_convocation', libelle: 'Joindre sa convocation (PDF)', aide: '— fabriquée au moment de l\'envoi, jamais conservée' },
                  { cle: 'joindre_ics', libelle: 'Joindre le rendez-vous (.ics)', aide: '— à ajouter à son agenda en un clic' }]
    },
    po_maitre: {
        icone: 'fa-briefcase', destinataire: "Maître de stage ou d'apprentissage, selon l'étudiant",
        sousTitre: 'Le passage de SON stagiaire ou apprenti — sa convocation et le rendez-vous joints',
        variables: [...VARS_COMMUNES, '{maitre}', '{qualite_maitre}', '{entreprise}'],
        ccSupplementaires: [{ cle: 'cc_etudiant', libelle: 'Cc : l\'étudiant' }, { cle: 'cc_tuteur', libelle: 'Cc : son tuteur pédagogique' }],
        options: [{ cle: 'joindre_convocation', libelle: 'Joindre sa convocation (PDF)', aide: '— un maître ne reçoit jamais le planning complet' },
                  { cle: 'joindre_ics', libelle: 'Joindre le rendez-vous (.ics)' }]
    },
    po_jury: {
        icone: 'fa-chalkboard-user', destinataire: 'Chaque enseignant du jury (un message personnel)',
        sousTitre: 'La liste de SES passages (en BUT1 : ses oraux et ses corrections, séparés)',
        variables: ['{intitule_oral}', '{liste_passages}', '{contact}', '{nom_utilisateur}'],
        ccSupplementaires: [],
        options: [{ cle: 'joindre_ics', libelle: 'Joindre les rendez-vous (.ics)', aide: '— un rendez-vous par passage' }]
    }
};
// PO.5b (point 5 validé) : les copies communes reçoivent UN récapitulatif de l'envoi, pas une copie par message.
export const LIBELLES_CC = { responsables: 'Cc : responsables (Équipe) — un récapitulatif', assistantes: 'Cc : assistantes (Équipe) — un récapitulatif' };
export const NOTE = "Les variables sont remplacées pour CHAQUE destinataire au moment de l'envoi (lot PO.5b) ; {salutation} et {e} suivent sa civilité, comme dans les convocations PDF.";

export function ModelesCourriel({ departementId, modifiable }) {
    const [modeles, setModeles] = useState(null);
    const [ouvert, setOuvert] = useState(null);
    const [erreur, setErreur] = useState(null);
    useEffect(() => {
        planningOrauxAPI.modelesCourriel(departementId).then((r) => setModeles(r.data.modeles_mail || []))
            .catch((e) => setErreur(e?.response?.data?.error || 'Les modèles de courriel ne se chargent pas.'));
    }, [departementId]);
    const majModeles = useCallback(({ modeles_mail }) => { if (modeles_mail) setModeles(modeles_mail); }, []);
    if (erreur) return <div className="po-erreur">{erreur}</div>;
    if (!modeles) return <div className="po-hint">Chargement…</div>;
    return (
        <>
            <div className="po-dd-note"><i className="fa-solid fa-circle-info" /><span>Communs à tous les plannings du département, avec leurs versions — <b>le même éditeur que le Suivi de stage</b>. Ils préremplissent la fenêtre d'envoi (lot PO.5b) ; ce qu'on y change vaut <b>pour un envoi seulement</b>. Les messages partent au nom de la personne qui envoie (« via DptQLIO Manager »).</span></div>
            {!modeles.length && <div className="po-erreur">Aucun modèle de courriel des oraux pour ce département : la migration v79 n'a pas été jouée.</div>}
            <div className="stg-root po-stg-hote po-courriels">
                {Object.entries(MODELES).map(([type, m]) => {
                    const modele = modeles.find((x) => x.type_mail === type);
                    if (!modele) return null;
                    return (
                        <AccordeonMail key={type} modele={modele} ouvert={ouvert === type} onBasculer={() => setOuvert(ouvert === type ? null : type)}
                            departementId={departementId} modifiable={modifiable} onMiseAJour={majModeles} api={planningOrauxAPI}
                            variables={m.variables} destinataire={m.destinataire} icone={m.icone} sousTitre={m.sousTitre} noteLien={NOTE}
                            ccSupplementaires={m.ccSupplementaires} options={m.options} libellesCc={LIBELLES_CC} />
                    );
                })}
            </div>
        </>
    );
}

const LIB_DEST = { candidat: 'Candidat', maitre: 'Maître', jury: 'Jury' };
const destDe = (e) => LIB_DEST[e.po_destinataire] || (/^\[Récapitulatif\]/.test(e.sujet || '') ? 'Récapitulatif' : '');
const LIB_RES = { envoye: ['ok', 'envoyé'], echec: ['ko', 'échec'], prepare: ['gris', 'préparé (messagerie)'] };
const quand = (v) => (v ? new Date(v).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '');

export function JournalEnvois({ departementId, planning }) {
    const [envois, setEnvois] = useState(null);
    const [erreur, setErreur] = useState(null);
    useEffect(() => {
        planningOrauxAPI.envois(planning.id, departementId).then((r) => setEnvois(r.data.envois || []))
            .catch((e) => setErreur(e?.response?.data?.error || 'Le journal ne se charge pas.'));
    }, [planning.id, departementId]);
    if (erreur) return <div className="po-erreur">{erreur}</div>;
    if (!envois) return <div className="po-hint">Chargement…</div>;
    return (
        <>
            <div className="po-dd-note"><i className="fa-solid fa-circle-info" /><span>Chaque message tel qu'il est parti (destinataires réels, texte, résultat) ; pour une convocation jointe, son empreinte — le fichier n'est pas conservé. Quand le planning change après un envoi, le bandeau « Prévenir les concernés » apparaît au-dessus.</span></div>
            <div className="po-card"><div className="po-card-body">
                {envois.map((e) => {
                    const [ton, lib] = LIB_RES[e.resultat] || ['gris', e.resultat || '—'];
                    return (
                        <div key={e.id} className="po-exp-doc">
                            <i className="fa-solid fa-paper-plane" />
                            <div className="po-ed-meta"><b>{e.sujet || '(sans objet)'}</b>
                                <span className="po-hint">{destDe(e)} · {e.destinataire || ''} · {quand(e.envoye_le || e.prepare_le)} · {e.prepare_par_nom || ''}</span></div>
                            <span className={'po-journal-res ' + ton}>{lib}</span>
                        </div>
                    );
                })}
                {!envois.length && <div className="po-hint" style={{ padding: '8px 0' }}>Aucun envoi pour ce planning. « Envoyer les convocations » arrive au lot PO.5b.</div>}
            </div></div>
        </>
    );
}
